import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useVenue } from "../VenueProvider";
import { Section, formatMXN, ACCENT } from "../cuentas/shared";
import { Kpi, ALERTA_COLOR } from "./shared";
import { carteraPorCobrar, carteraDeudas } from "../../utils/reportes";
import { formatCalendarDate } from "../../utils/dates";
import {
  BanknotesIcon,
  ReceiptPercentIcon,
  ClockIcon,
} from "@heroicons/react/20/solid";

// La antigüedad de los saldos. Cuentas ya dice cuánto te deben y a quién le
// debes; lo que no dice es desde cuándo, que es lo que separa un saldo sano
// de uno incobrable.
//
// Este reporte NO se acota al período: una cartera es una foto de hoy. El
// selector de período no lo controla a propósito — se navega con sus
// propios controles (ver registry.js).
const COLOR_BUCKET = {
  porVencer: "#94a3b8",
  d0: "#22c55e",
  d30: "#f59e0b",
  d60: "#f97316",
  d90: ALERTA_COLOR,
};

const BarraBuckets = ({ buckets, total }) => (
  <div>
    <div className="flex h-3 w-full overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-700/60">
      {buckets.map((b) =>
        b.total > 0 ? (
          <div
            key={b.key}
            style={{
              width: `${total > 0 ? (b.total / total) * 100 : 0}%`,
              backgroundColor: COLOR_BUCKET[b.key],
            }}
            title={`${b.label}: ${formatMXN(b.total)}`}
          />
        ) : null
      )}
    </div>
    <div className="mt-3 space-y-1.5">
      {buckets.map((b) => (
        <div key={b.key} className="flex items-center gap-2 text-sm">
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: COLOR_BUCKET[b.key] }}
          />
          <span className="text-zinc-500">{b.label}</span>
          <span className="text-xs text-zinc-400">
            {b.count > 0 ? `· ${b.count}` : ""}
          </span>
          <span className="ml-auto font-medium text-zinc-900 dark:text-white whitespace-nowrap">
            {b.total > 0 ? formatMXN(b.total) : "—"}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const edadLabel = (dias) => {
  if (dias == null) return "sin fecha";
  if (dias < 0) return `en ${Math.abs(dias)} d`;
  if (dias === 0) return "hoy";
  return `${dias} d`;
};

const ReporteCartera = () => {
  const navigate = useNavigate();
  const { events, costos, proveedores, calendarTimezone: tz } = useVenue();
  const [verTodo, setVerTodo] = useState(false);

  const hoy = useMemo(() => new Date(), []);
  const cobrar = useMemo(
    () => carteraPorCobrar({ events, hoy, timeZone: tz }),
    [events, hoy, tz]
  );
  const deudas = useMemo(
    () => carteraDeudas({ costos, hoy, timeZone: tz }),
    [costos, hoy, tz]
  );

  const nombreProveedor = useMemo(() => {
    const map = {};
    (proveedores || []).forEach((p) => {
      if (p?.id) map[p.id] = p.nombre || "";
    });
    return map;
  }, [proveedores]);

  const TOPE = 12;
  const filasCobrar = verTodo ? cobrar.filas : cobrar.filas.slice(0, TOPE);
  const filasDeudas = verTodo ? deudas.filas : deudas.filas.slice(0, TOPE);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi
          label="Por cobrar"
          value={formatMXN(cobrar.total)}
          hint={`${cobrar.filas.length} ${
            cobrar.filas.length === 1 ? "evento" : "eventos"
          } con saldo`}
        />
        <Kpi
          label="Vencido"
          value={formatMXN(cobrar.vencido)}
          hint="el evento ya pasó y sigue sin liquidarse"
          tone={cobrar.vencido > 0 ? "text-red-600" : ""}
        />
        <Kpi
          label="Debes"
          value={formatMXN(deudas.total)}
          hint={`${deudas.filas.length} ${
            deudas.filas.length === 1 ? "costo" : "costos"
          } por pagar`}
        />
        <Kpi
          label="Posición neta"
          value={formatMXN(cobrar.total - deudas.total)}
          hint="lo que te deben menos lo que debes"
          tone={cobrar.total - deudas.total < 0 ? "text-red-600" : "text-emerald-600"}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <Section icon={BanknotesIcon} title="Por cobrar, por antigüedad">
          {cobrar.filas.length === 0 ? (
            <p className="text-sm text-zinc-400">
              Nada por cobrar: todos los eventos con valor están liquidados.
            </p>
          ) : (
            <>
              <BarraBuckets buckets={cobrar.buckets} total={cobrar.total} />
              <div className="mt-5 space-y-1.5">
                {filasCobrar.map((f) => (
                  <div key={f.id} className="flex items-baseline gap-2 text-sm">
                    <span
                      className="w-14 shrink-0 text-xs font-medium"
                      style={{ color: COLOR_BUCKET[f.bucket] }}
                    >
                      {edadLabel(f.dias)}
                    </span>
                    <button
                      type="button"
                      onClick={() => navigate(`/dashboard/eventos/${f.id}`)}
                      className="truncate hover:underline text-left"
                      style={{ color: ACCENT }}
                      title={f.nombre}
                    >
                      {f.nombre}
                    </button>
                    <span className="hidden sm:inline text-xs text-zinc-400 whitespace-nowrap">
                      {formatCalendarDate(f.fecha, tz, { style: "short" })}
                    </span>
                    <span className="ml-auto font-medium text-zinc-900 dark:text-white whitespace-nowrap">
                      {formatMXN(f.saldo)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Section>

        <Section icon={ReceiptPercentIcon} title="Lo que debes, por antigüedad">
          {deudas.filas.length === 0 ? (
            <p className="text-sm text-zinc-400">
              No debes nada: todos los costos capturados están pagados.
            </p>
          ) : (
            <>
              <BarraBuckets buckets={deudas.buckets} total={deudas.total} />
              <div className="mt-5 space-y-1.5">
                {filasDeudas.map((f) => (
                  <div key={f.id} className="flex items-baseline gap-2 text-sm">
                    <span
                      className="w-14 shrink-0 text-xs font-medium"
                      style={{ color: COLOR_BUCKET[f.bucket] }}
                    >
                      {edadLabel(f.dias)}
                    </span>
                    {f.proveedorId ? (
                      <button
                        type="button"
                        onClick={() => navigate(`/dashboard/proveedores/${f.proveedorId}`)}
                        className="truncate hover:underline text-left"
                        style={{ color: ACCENT }}
                      >
                        {f.proveedor || nombreProveedor[f.proveedorId] || f.concepto}
                      </button>
                    ) : (
                      <span className="truncate text-zinc-600 dark:text-zinc-300" title={f.concepto}>
                        {f.concepto}
                      </span>
                    )}
                    <span className="hidden sm:inline text-xs text-zinc-400 whitespace-nowrap">
                      {f.categoria}
                    </span>
                    <span className="ml-auto font-medium text-red-600 whitespace-nowrap">
                      {formatMXN(f.saldo)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Section>
      </div>

      {(cobrar.filas.length > TOPE || deudas.filas.length > TOPE) && (
        <div className="flex items-center justify-center gap-2 text-sm">
          <button
            type="button"
            onClick={() => setVerTodo((v) => !v)}
            className="font-semibold hover:underline"
            style={{ color: ACCENT }}
          >
            {verTodo ? "Ver solo los más viejos" : "Ver todos los renglones"}
          </button>
        </div>
      )}

      <div className="flex items-center gap-2 text-xs text-zinc-400">
        <ClockIcon className="h-4 w-4 shrink-0" />
        <span>
          La cartera es una foto de hoy, no del período: lo que te deben
          envejece desde la fecha del evento y lo que debes desde la fecha del
          costo. Para gestionarla, Cuentas → Por cobrar y Cuentas → Por pagar.
        </span>
      </div>
    </div>
  );
};

export default ReporteCartera;
